import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/cn';

// Selo de estado. Desde 17/10/2026 (Caio aprovou a variante "ousada"): o estado pinta o
// texto, o quadrado (`dot`) e um tom CHAPADO de fundo (`--*-soft`, a cor misturada ao
// cartão), sem borda, faixa nem degradê. Verde é deu certo, azul é em andamento, laranja pede
// olho, vermelho é erro. `paid` é o mesmo verde de `success` (antes era o ouro da marca: ouro
// ficou pra dinheiro e reserva, não pra estado). `warning` é o "em andamento" (aguardando,
// processando, na fila): o nome ficou por compatibilidade, a cor é a de `--info`.
const badgeVariants = cva(
  'inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border border-transparent px-2.5 text-xs font-medium whitespace-nowrap transition-colors duration-150 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [--dot:var(--muted-foreground)] [a&]:hover:bg-accent',
        destructive: 'bg-destructive-soft text-destructive [--dot:var(--destructive)]',
        success: 'bg-success-soft text-success [--dot:var(--success)]',
        warning: 'bg-info-soft text-info [--dot:var(--info)]',
        attention: 'bg-attention-soft text-attention [--dot:var(--attention)]',
        paid: 'bg-success-soft text-success [--dot:var(--success)]',
        outline: 'text-foreground shadow-(--shadow-inset) [a&]:hover:bg-secondary',
        ghost: '[a&]:hover:bg-secondary [a&]:hover:text-foreground',
        link: 'text-teal underline-offset-4 [a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  dot = false,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; dot?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {dot && !asChild && (
        <span aria-hidden="true" className="size-[7px] shrink-0 bg-(--dot,currentColor)" />
      )}
      {children}
    </Comp>
  );
}

export { Badge, badgeVariants };
